<template>
  <div v-if="isOpen" class="modal fade show" style="display: block; background: #1c1d1c33">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
      <div class="modal-content">
        <div class="modal-header py-1 px-2">
          <h1 class="modal-title-text mb-0">Asset History</h1>
          <button
              @click="onClose"
              type="button"
              class="btn-close"
              aria-label="Close"
          ></button>
        </div>

        <div class="modal-body pb-2 px-2">
          <div v-if="loading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div v-if="!loading && !hasData" class="text-center py-3">
            No history found
          </div>

          <div v-if="!loading && hasData">
            <div class="asset-head d-flex justify-content-between align-items-start flex-wrap gap-1 mb-2">
              <div>
                <h4 class="asset-title mb-1">{{ asset.name }}</h4>
                <div class="asset-meta d-flex flex-wrap align-items-center">
                  <span class="meta-chip">ID <b>{{ asset.id }}</b></span>
                  <span class="meta-chip">Class <b>{{ textOrDash(asset.product_name) }}</b></span>
                  <span class="meta-chip">Machine <b>{{ textOrDash(asset.machine_id) }}</b></span>
                </div>
              </div>
              <span class="badge status-badge" :class="statusClass(asset.status)">
                {{ statusLabel(asset.status) }}
              </span>
            </div>

            <div class="row gy-1 mb-2">
              <div class="col-6 col-md-3">
                <div class="summary-tile">
                  <span class="tile-label">Asset Value</span>
                  <span class="tile-value">{{ money(summary.asset_value) }}</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="summary-tile tile-accent-danger">
                  <span class="tile-label">Accumulated Depreciation</span>
                  <span class="tile-value">{{ money(summary.accumulated_depreciation) }}</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="summary-tile tile-accent-success">
                  <span class="tile-label">Written Down Value</span>
                  <span class="tile-value">{{ money(summary.written_down_value) }}</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="summary-tile">
                  <span class="tile-label">Transfers / Charges</span>
                  <span class="tile-value">{{ summary.transfer_count }} / {{ chargedRunCount }}</span>
                </div>
              </div>
            </div>

            <ul class="nav asset-tabs" role="tablist">
              <li class="nav-item" v-for="tab in tabs" :key="tab.key">
                <a
                    class="nav-link"
                    :class="{ active: activeTab === tab.key }"
                    href="#"
                    @click.prevent="activeTab = tab.key"
                >{{ tab.label }}</a>
              </li>
            </ul>

            <div v-if="activeTab === 'movement'" class="table-responsive mt-1">
              <table class="table table-bordered table-sm mb-0">
                <thead>
                <tr>
                  <th class="nowrap">Txn Number</th>
                  <th class="nowrap">Type</th>
                  <th class="nowrap">Date</th>
                  <th class="nowrap">Custodian</th>
                  <th class="nowrap">Location</th>
                  <th class="nowrap">Project</th>
                  <th class="nowrap">Cost Centre</th>
                  <th class="nowrap">Business Unit</th>
                  <th class="nowrap text-end">Asset In</th>
                  <th class="nowrap text-end">Asset Out</th>
                  <th class="nowrap">Note</th>
                  <th class="nowrap">Updated By</th>
                  <th class="nowrap">Logged At</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="log in logs" :key="log.id">
                  <td class="nowrap">{{ textOrDash(log.txn_number) }}</td>
                  <td class="nowrap">
                    <span class="badge" :class="typeClass(log.type)">{{ movementLabel(log) }}</span>
                  </td>
                  <td class="nowrap">{{ textOrDash(log.format_capitalization_date) }}</td>
                  <td>{{ textOrDash(log.contact_profile_name) }}</td>
                  <td>{{ textOrDash(log.business_location_name) }}</td>
                  <td>{{ textOrDash(log.project_master_name) }}</td>
                  <td>{{ textOrDash(log.cost_centre_name) }}</td>
                  <td>{{ textOrDash(log.business_name) }}</td>
                  <td class="nowrap text-end">{{ moneyOrDash(log.asset_in) }}</td>
                  <td class="nowrap text-end">{{ moneyOrDash(log.asset_out) }}</td>
                  <td>{{ textOrDash(log.note) }}</td>
                  <td class="nowrap">{{ textOrDash(log.updated_by_name) }}</td>
                  <td class="nowrap">{{ textOrDash(log.format_created_at) }}</td>
                </tr>
                </tbody>
              </table>
              <p v-if="!logs.length" class="text-center mt-2">No movement log found</p>
            </div>

            <div v-if="activeTab === 'depreciation'" class="table-responsive mt-1">
              <p class="pane-note mb-1">
                A period with transfers is split into one row per custody segment, so each charge is
                attributable to the project and cost centre that held the asset for those days.
              </p>
              <table class="table table-bordered table-sm mb-0">
                <thead>
                <tr>
                  <th class="nowrap">Segment From</th>
                  <th class="nowrap">Segment To</th>
                  <th class="nowrap text-end">Days</th>
                  <th class="nowrap">Type</th>
                  <th class="nowrap">Project</th>
                  <th class="nowrap">Cost Centre</th>
                  <th class="nowrap">Business Unit</th>
                  <th class="nowrap text-end">Opening WDV</th>
                  <th class="nowrap text-end">Dep. Rate</th>
                  <th class="nowrap text-end">Depreciation In</th>
                  <th class="nowrap text-end">Depreciation Out</th>
                  <th class="nowrap text-end">Tax Rate</th>
                  <th class="nowrap text-end">Tax Dep. In</th>
                  <th class="nowrap">Txn Number</th>
                </tr>
                </thead>
                <tbody>
                <template v-for="group in scheduleGroups" :key="group.key">
                  <tr v-if="group.isSplit" class="table-light">
                    <td colspan="14" class="fw-bold">
                      {{ group.periodLabel }} — split across {{ group.rows.length }} custody segments,
                      total {{ group.total }}
                    </td>
                  </tr>
                  <tr v-for="row in group.rows" :key="row.id">
                    <td class="nowrap">{{ row.fromDate }}</td>
                    <td class="nowrap">{{ row.toDate }}</td>
                    <td class="nowrap text-end">{{ row.days }}</td>
                    <td class="nowrap">
                      <span class="badge" :class="row.badgeClass">{{ row.typeLabel }}</span>
                    </td>
                    <td>{{ row.project }}</td>
                    <td>{{ row.costCentre }}</td>
                    <td>{{ row.business }}</td>
                    <td class="nowrap text-end">{{ row.openingWdv }}</td>
                    <td class="nowrap text-end">{{ row.rate }}</td>
                    <td class="nowrap text-end">{{ row.charge }}</td>
                    <td class="nowrap text-end">{{ row.chargeOut }}</td>
                    <td class="nowrap text-end">{{ row.taxRate }}</td>
                    <td class="nowrap text-end">{{ row.taxCharge }}</td>
                    <td class="nowrap">{{ row.txn }}</td>
                  </tr>
                </template>
                </tbody>
                <tfoot v-if="depreciations.length">
                <tr class="fw-bold">
                  <td colspan="9" class="text-end">Total</td>
                  <td class="nowrap text-end">{{ money(depreciationTotals.depreciation_in) }}</td>
                  <td class="nowrap text-end">{{ money(depreciationTotals.depreciation_out) }}</td>
                  <td></td>
                  <td class="nowrap text-end">{{ money(depreciationTotals.tax_depreciation_in) }}</td>
                  <td></td>
                </tr>
                </tfoot>
              </table>
              <p v-if="!depreciations.length" class="text-center mt-2">No depreciation charged yet</p>
            </div>

            <div v-if="activeTab === 'segment'" class="table-responsive mt-1">
              <p class="pane-note mb-1">
                Every custody segment listed separately, with the share of total depreciation each one absorbed.
              </p>
              <table class="table table-bordered table-sm mb-0">
                <thead>
                <tr>
                  <th class="nowrap">Segment From</th>
                  <th class="nowrap">Segment To</th>
                  <th class="nowrap text-end">Days</th>
                  <th class="nowrap">Project</th>
                  <th class="nowrap">Cost Centre</th>
                  <th class="nowrap">Business Unit</th>
                  <th class="nowrap">Custodian</th>
                  <th class="nowrap">Location</th>
                  <th class="nowrap text-end">Depreciation</th>
                  <th class="nowrap text-end">Share</th>
                  <th class="nowrap">Txn Number</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in segmentShares" :key="row.id">
                  <td class="nowrap">{{ row.fromDate }}</td>
                  <td class="nowrap">{{ row.toDate }}</td>
                  <td class="nowrap text-end">{{ row.days }}</td>
                  <td>{{ row.project }}</td>
                  <td>{{ row.costCentre }}</td>
                  <td>{{ row.business }}</td>
                  <td>{{ row.custodian }}</td>
                  <td>{{ row.location }}</td>
                  <td class="nowrap text-end">{{ money(row.charge) }}</td>
                  <td class="nowrap text-end">{{ row.share }}</td>
                  <td class="nowrap">{{ row.txn }}</td>
                </tr>
                </tbody>
                <tfoot v-if="segmentShares.length">
                <tr class="fw-bold">
                  <td colspan="8" class="text-end">Total</td>
                  <td class="nowrap text-end">{{ money(depreciationTotals.depreciation_in) }}</td>
                  <td class="nowrap text-end">100.00%</td>
                  <td></td>
                </tr>
                </tfoot>
              </table>
              <p v-if="!segmentShares.length" class="text-center mt-2">No depreciation charged yet</p>
            </div>

            <div v-if="activeTab === 'details'" class="mt-1">
              <div class="row gy-1">
                <div class="col-12 col-md-6" v-for="section in detailSections" :key="section.title">
                  <table class="table table-bordered table-sm mb-0">
                    <thead>
                    <tr>
                      <th colspan="2">{{ section.title }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in section.rows" :key="row.label">
                      <td class="fw-bold detail-label">{{ row.label }}</td>
                      <td>{{ row.value }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer py-1 justify-content-center">
          <button @click="onClose" type="button" class="btn btn-outline-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, inject } from 'vue'
import { useRoute } from 'vue-router'
import handleAssetRegister from '@/services/modules/assetRegister/assetRegister'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  assetId: { type: [Number, String], default: null }
})

const emit = defineEmits(['onClose'])

const route = useRoute()
const showError = inject('showError')

const { fetchAssetRegisterHistory } = handleAssetRegister()

const loading = ref(false)
const activeTab = ref('movement')
const asset = ref({})
const logs = ref([])
const depreciations = ref([])
const summary = ref({})

const tabs = [
  { key: 'movement', label: 'Movement History' },
  { key: 'depreciation', label: 'Depreciation History' },
  { key: 'segment', label: 'Segment Wise Share' },
  { key: 'details', label: 'Full Details' }
]

const typeLabels = {
  acquisition: 'Acquisition',
  transfer: 'Transfer',
  disposal: 'Disposal',
  depreciation: 'Depreciation'
}

const statusLabels = {
  active: 'Active',
  inactive: 'Inactive',
  asset_disposal: 'Asset Disposal',
  asset_transfer: 'Asset Transfer'
}

const hasData = computed(() => {
  if (!asset.value) return false
  return !!asset.value.id
})

const chargeRows = computed(() => depreciations.value.filter(row => row.type !== 'disposal'))

const chargedRunCount = computed(() => chargeRows.value.filter(row => Number(row.depreciation_in) > 0).length)

const depreciationTotals = computed(() => {
  const totals = {
    depreciation_in: 0,
    depreciation_out: 0,
    tax_depreciation_in: 0,
    tax_depreciation_out: 0
  }
  depreciations.value.forEach(row => {
    totals.depreciation_in += Number(row.depreciation_in) || 0
    totals.depreciation_out += Number(row.depreciation_out) || 0
    totals.tax_depreciation_in += Number(row.tax_depreciation_in) || 0
    totals.tax_depreciation_out += Number(row.tax_depreciation_out) || 0
  })
  return totals
})

const scheduleGroups = computed(() => {
  const groups = []
  const index = {}
  depreciations.value.forEach(row => {
    const key = groupKeyFor(row)
    if (index[key] === undefined) {
      index[key] = groups.length
      groups.push({ key, periodLabel: periodLabelFor(row), rows: [], sum: 0 })
    }
    const group = groups[index[key]]
    group.sum += Number(row.depreciation_in) || 0
    group.rows.push(scheduleRow(row))
  })
  return groups.map(group => {
    return Object.assign(group, {
      isSplit: group.rows.length > 1,
      total: money(group.sum)
    })
  })
})

const segmentShares = computed(() => {
  const total = depreciationTotals.value.depreciation_in
  return chargeRows.value.map(row => {
    const charge = Number(row.depreciation_in) || 0
    return {
      id: row.id,
      fromDate: textOrDash(row.format_from_date),
      toDate: textOrDash(row.format_to_date),
      days: textOrDash(row.segment_days),
      project: textOrDash(row.project_master_name),
      costCentre: textOrDash(row.cost_centre_name),
      business: textOrDash(row.business_name),
      custodian: textOrDash(row.contact_profile_name),
      location: textOrDash(row.business_location_name),
      charge,
      share: shareOf(charge, total),
      txn: textOrDash(row.txn_number)
    }
  })
})

function shareOf(charge, total) {
  if (total <= 0) return '0.00%'
  return `${(charge / total * 100).toFixed(2)}%`
}

function groupKeyFor(row) {
  if (row.type === 'disposal') return `disposal-${row.id}`
  if (!row.process_tracker_id) return `period-${row.format_from_date}`
  return `period-${row.process_tracker_id}`
}

function periodLabelFor(row) {
  if (!row.period_start) return `${textOrDash(row.format_from_date)} to ${textOrDash(row.format_to_date)}`
  return `${dateOrDash(row.period_start)} to ${dateOrDash(row.period_end)}`
}

function scheduleRow(row) {
  return {
    id: row.id,
    fromDate: textOrDash(row.format_from_date),
    toDate: textOrDash(row.format_to_date),
    days: textOrDash(row.segment_days),
    typeLabel: typeLabel(row.type),
    badgeClass: typeClass(row.type),
    project: textOrDash(row.project_master_name),
    costCentre: textOrDash(row.cost_centre_name),
    business: textOrDash(row.business_name),
    openingWdv: moneyOrDash(row.opening_wdv),
    rate: rateOrDash(row.depreciation_rate),
    charge: moneyOrDash(row.depreciation_in),
    chargeOut: moneyOrDash(row.depreciation_out),
    taxRate: rateOrDash(row.tax_depreciation_rate),
    taxCharge: moneyOrDash(row.tax_depreciation_in),
    txn: textOrDash(row.txn_number)
  }
}

const detailSections = computed(() => {
  const item = asset.value
  return [
    {
      title: 'Identification',
      rows: [
        { label: 'Asset ID', value: textOrDash(item.id) },
        { label: 'Name', value: textOrDash(item.name) },
        { label: 'Asset Class', value: textOrDash(item.product_name) },
        { label: 'Machine ID', value: textOrDash(item.machine_id) },
        { label: 'Model', value: textOrDash(item.model) },
        { label: 'Manufacturer', value: textOrDash(item.manufacturer) },
        { label: 'Manufacturing Date', value: dateOrDash(item.manufacturing_date) },
        { label: 'Status', value: statusLabel(item.status) }
      ]
    },
    {
      title: 'Assignment',
      rows: [
        { label: 'Custodian', value: textOrDash(item.contact_profile_name) },
        { label: 'Location', value: textOrDash(item.business_location_name) },
        { label: 'Department', value: textOrDash(item.department_name) },
        { label: 'Project', value: textOrDash(item.project_master_name) },
        { label: 'Cost Centre', value: textOrDash(item.cost_centre_name) },
        { label: 'Business Unit', value: textOrDash(item.business_name) },
        { label: 'Supplier', value: textOrDash(item.supplier_name) }
      ]
    },
    {
      title: 'Acquisition & Value',
      rows: [
        { label: 'Capitalization Date', value: dateOrDash(item.capitalization_date) },
        { label: 'Bill Date', value: dateOrDash(item.bill_date) },
        { label: 'Bill Number', value: textOrDash(item.bill_number) },
        { label: 'Bill Qty', value: textOrDash(item.bill_qty) },
        { label: 'Asset Value', value: moneyOrDash(item.asset_value) },
        { label: 'Scrap Value', value: moneyOrDash(item.residual_value) },
        { label: 'Grant / Donation', value: textOrDash(item.grant_donation) },
        { label: 'Grant / Donation Details', value: textOrDash(item.grant_donation_details) }
      ]
    },
    {
      title: 'Depreciation & Lifecycle',
      rows: [
        { label: 'Depreciation Rate', value: rateOrDash(item.depreciation_rate) },
        { label: 'Tax Depreciation Rate', value: rateOrDash(item.tax_depreciation_rate) },
        { label: 'Useful Life', value: textOrDash(item.useful_life) },
        { label: 'Installation Date', value: dateOrDash(item.installation_date) },
        { label: 'Warranty Period', value: textOrDash(item.warranty_period) },
        { label: 'Maintenance Schedule', value: dateOrDash(item.maintenance_schedule) },
        { label: 'Asset Condition', value: textOrDash(item.asset_condition) },
        { label: 'Insurance Details', value: textOrDash(item.insurance_details) },
        { label: 'Lease Details', value: textOrDash(item.lease_details) },
        { label: 'Description', value: textOrDash(item.asset_description) }
      ]
    }
  ]
})

function getQuery() {
  return `?company_id=${route.params.companyId}`
}

function textOrDash(value) {
  if (value === null || value === undefined || value === '') return '-'
  return value
}

function money(value) {
  const amount = Number(value) || 0
  return amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function moneyOrDash(value) {
  if (value === null || value === undefined || value === '') return '-'
  return money(value)
}

function rateOrDash(value) {
  if (value === null || value === undefined || value === '') return '-'
  return `${value}%`
}

function dateOrDash(value) {
  if (!value) return '-'
  const parts = String(value).split('T')[0].split('-')
  if (parts.length < 3) return value
  return `${parts[2]}-${parts[1]}-${parts[0]}`
}

function typeLabel(type) {
  if (!type) return '-'
  if (!typeLabels[type]) return type
  return typeLabels[type]
}

function statusLabel(status) {
  if (!status) return '-'
  if (!statusLabels[status]) return status
  return statusLabels[status]
}

function movementLabel(log) {
  if (log.type !== 'transfer') return typeLabel(log.type)
  if (Number(log.asset_in) > 0) return 'Transfer In'
  return 'Transfer Out'
}

function typeClass(type) {
  if (type === 'acquisition') return 'bg-primary'
  if (type === 'transfer') return 'bg-info'
  if (type === 'disposal') return 'bg-danger'
  return 'bg-secondary'
}

function statusClass(status) {
  if (status === 'active') return 'bg-success'
  if (status === 'asset_disposal') return 'bg-danger'
  if (status === 'asset_transfer') return 'bg-info'
  return 'bg-secondary'
}

function resetState() {
  asset.value = {}
  logs.value = []
  depreciations.value = []
  summary.value = {}
  activeTab.value = 'movement'
}

function loadHistory() {
  if (!props.assetId) return
  loading.value = true
  fetchAssetRegisterHistory(props.assetId, getQuery())
    .then(res => {
      loading.value = false
      if (!res.status) {
        showError(res.message)
        return
      }
      asset.value = res.data.asset
      logs.value = res.data.logs
      depreciations.value = res.data.depreciations
      summary.value = res.data.summary
    })
    .catch(() => {
      loading.value = false
    })
}

watch(() => props.isOpen, (open) => {
  if (!open) return
  resetState()
  loadHistory()
})

function onClose() {
  resetState()
  emit('onClose')
}
</script>

<style scoped>
.nowrap {
  white-space: nowrap;
}
.modal-dialog-scrollable {
  max-height: calc(100vh - 3.5rem);
}
.modal-dialog-scrollable .modal-content {
  max-height: calc(100vh - 3.5rem);
  overflow: hidden;
}
.modal-body {
  overflow-y: auto;
}
.modal-body .table-responsive {
  max-height: 55vh;
  overflow: auto;
}
.modal-body .table-responsive thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f3f2f7;
}
.modal-body .table-responsive tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #f3f2f7;
}
.modal-title-text {
  color: #000000;
  font-size: 20px;
  font-weight: 700;
}
.asset-head {
  border-bottom: 1px solid #ebe9f1;
  padding-bottom: 10px;
}
.asset-title {
  color: #000000;
  font-size: 18px;
  font-weight: 700;
}
.asset-meta {
  gap: 6px;
}
.meta-chip {
  background: #f3f2f7;
  border-radius: 4px;
  padding: 3px 9px;
  font-size: 12px;
  color: #000000;
}
.meta-chip b {
  font-weight: 700;
}
.status-badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 5px 11px;
}
.summary-tile {
  border: 1px solid #ebe9f1;
  border-left: 3px solid #7367f0;
  border-radius: 6px;
  padding: 9px 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: #ffffff;
}
.tile-accent-danger {
  border-left-color: #ea5455;
}
.tile-accent-success {
  border-left-color: #28c76f;
}
.tile-label {
  color: #000000;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.tile-value {
  color: #000000;
  font-size: 17px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.pane-note {
  color: #000000;
  font-size: 12px;
  background: #f8f8fb;
  border-left: 3px solid #7367f0;
  border-radius: 4px;
  padding: 7px 11px;
}
.detail-label {
  width: 45%;
}
.asset-tabs {
  gap: 4px;
  background: #f3f2f7;
  border-radius: 6px;
  padding: 4px;
  flex-wrap: wrap;
}
.asset-tabs .nav-link {
  color: #000000;
  font-weight: 600;
  font-size: 13px;
  border: 0;
  border-radius: 4px;
  padding: 7px 14px;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.asset-tabs .nav-link:hover {
  background: #e6e4f0;
  color: #000000;
}
.asset-tabs .nav-link.active {
  background: #ffffff;
  color: #000000;
  box-shadow: 0 1px 3px rgba(34, 41, 47, 0.16);
}
.asset-tabs .nav-link:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: -2px;
}
.modal-content :deep(.table) {
  color: #000000;
}
.modal-content :deep(.table > thead th) {
  color: #000000;
  font-weight: 700;
}
.modal-content :deep(.table > tbody td) {
  color: #000000;
}
</style>
